import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import type { Trust } from "@thalamos/aspire-common";

import { CreateEditTrustModal } from "./CreateEditTrustModal.js";
import { api, apiHooks } from "~/api.js";
import {
  Banner,
  BannerList,
  Button,
  ButtonIcon,
  LoadingSpinner,
  PopupDialog,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

export function DeleteTrustModal({
  open,
  onClose,
  trust,
  handleSubmit,
}: {
  open: boolean;
  onClose: () => void;
  trust: Trust;
  handleSubmit: () => Promise<void>;
}) {
  return (
    <PopupDialog open={open} onClose={onClose}>
      <DialogTitle>
        Delete Trust
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to delete this trust?</Typography>
        <Typography sx={{ fontWeight: "bold", margin: "2em 0" }}>
          {trust.name}
          <br />
          {trust.address}
          <br />
          {trust.postalCode}
        </Typography>
        <Banner
          bannerType={BannerList.WARNING}
          body="This action cannot be undone."
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", mt: 2 }}>
        <Button label="Cancel" onClick={onClose} variant="outlined" />
        <Button
          label="Delete"
          onClick={() => {
            void handleSubmit();
          }}
          color="error"
        />
      </DialogActions>
    </PopupDialog>
  );
}

function TrustManagementRow({
  trust,
  refetchTrusts,
}: {
  trust: Trust;
  refetchTrusts: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showDeleteTrustModal, setShowDeleteTrustModal] = useState(false);
  const [showCreateEditTrustModal, setShowCreateEditTrustModal] =
    useState(false);
  const open = Boolean(anchorEl);

  return (
    <>
      <TableRow key={trust.id}>
        <TableCell>{trust.name}</TableCell>
        <TableCell>{trust.address}</TableCell>
        <TableCell>{trust.postalCode}</TableCell>
        <TableCell>{trust.emhaOrgId}</TableCell>
        <TableCell>{trust.odsCode}</TableCell>
        <TableCell>
          <Button
            variant="outlined"
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
            label="Action"
          />
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => {
              setAnchorEl(null);
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem
              onClick={() => {
                setShowCreateEditTrustModal(true);
              }}
            >
              <ListItemIcon>
                <Edit fontSize="small" color="primary" />
              </ListItemIcon>
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowDeleteTrustModal(true);
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" color="primary" />
              </ListItemIcon>
              Delete
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>

      {showDeleteTrustModal && (
        <DeleteTrustModal
          open={showDeleteTrustModal}
          onClose={() => {
            setShowDeleteTrustModal(false);
          }}
          trust={trust}
          handleSubmit={async () => {
            try {
              const result = await api.trust.delete(trust.id);
              if (result.status !== 204) {
                renderErrorToast({
                  message: `Failed to delete trust. ${(result.data as any).message ?? "Unknown"}`,
                });
              } else {
                renderSuccessToast({
                  message: `Trust "${trust.name}" deleted successfully.`,
                });
              }
            } catch {
              renderErrorToast({ message: "Failed to delete trust." });
            }
            setShowDeleteTrustModal(false);
            refetchTrusts();
          }}
        />
      )}

      {showCreateEditTrustModal && (
        <CreateEditTrustModal
          open={showCreateEditTrustModal}
          onClose={() => {
            setShowCreateEditTrustModal(false);
          }}
          refetchTrusts={() => {
            refetchTrusts();
          }}
          trust={trust}
        />
      )}
    </>
  );
}

export function TrustManagementPage() {
  const [trustFilterQuery, setTrustFilterQuery] = useState("");
  const [showCreateEditTrustModal, setShowCreateEditTrustModal] =
    useState(false);
  const [page, setPage] = useState(0);
  const trustPageSize = 10;

  const [{ data: trusts }, refetchTrusts] = apiHooks.trust.search(
    trustFilterQuery,
    trustPageSize,
    page * trustPageSize,
  );

  const numPages = trusts?.count ? Math.ceil(trusts.count / trustPageSize) : 1;

  return (
    <Box>
      <Box sx={{ display: "flex", width: "100%", my: 2 }}>
        <TextField
          placeholder="Filter trusts..."
          sx={{ width: "100%" }}
          value={trustFilterQuery}
          onChange={(e) => {
            setTrustFilterQuery(e.target.value);
            setPage(0);
          }}
        />
        <Box
          sx={{
            ml: 2,
            width: "10em",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            label="Add Trust"
            endIcon={ButtonIcon.add}
            onClick={() => {
              setShowCreateEditTrustModal(true);
            }}
          />
        </Box>
      </Box>

      {!trusts ? (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                if (page > 0) setPage(page - 1);
              }}
              label="&#60;"
            />
            <Box sx={{ mx: 2 }}>
              Page {page + 1} of {numPages}
            </Box>
            <Button
              variant="outlined"
              onClick={() => {
                if (page < numPages - 1) setPage(page + 1);
              }}
              label="&#62;"
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Postcode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>EMHA Org ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>ODS Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trusts.results.map((trust) => (
                  <TrustManagementRow
                    key={trust.id}
                    trust={trust}
                    refetchTrusts={() => {
                      void refetchTrusts();
                    }}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {showCreateEditTrustModal && (
        <CreateEditTrustModal
          open={showCreateEditTrustModal}
          onClose={() => {
            setShowCreateEditTrustModal(false);
          }}
          refetchTrusts={() => {
            void refetchTrusts();
          }}
        />
      )}
    </Box>
  );
}
